import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, RequestStatus } from "../types";
import { getUnreadCount } from "../utils/notifications";
import apiService from "../../../service/Api";
import { isAdmin } from "../utils/permissions";
import {
  ModernStatusBadge,
  ModernMethodChip,
  ModernPriorityChip,
  ModernStatCard,
} from "./ModernUiComponents";

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

interface AdminDashboardProps {
  requests: EndpointRequest[];
  onRequestUpdate?: (id: string, updates: Partial<EndpointRequest>) => void;
  onViewRequest: (request: EndpointRequest) => void;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({
  requests,
  onViewRequest,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const [activeTab, setActiveTab] = useState<"requested" | "completed">("requested");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "all">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openPointsDialog, setOpenPointsDialog] = useState(false);
  const [selectedDeveloper, setSelectedDeveloper] = useState<string>("");
  const [pointsToAward, setPointsToAward] = useState<number>(0);
  const [pointsReason, setPointsReason] = useState<string>("");
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [stats, setStats] = useState({
    requested: 0,
    completed: 0,
    total: 0,
  });
  const [leaderboard, setLeaderboard] = useState<{
    date_awarded: string;
    leaderboard: Array<{
      user_id: number;
      name: string;
      requests_count: number;
      awarded_points: number;
    }>;
  } | null>(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  const userIsAdmin = isAdmin(user);

  useEffect(() => {
    if (!userIsAdmin) return;
    const updateCount = () => {
      setNotificationCount(getUnreadCount("admin"));
    };
    updateCount();
    window.addEventListener("endpoint-request-notification-count-updated", updateCount);
    return () => {
      window.removeEventListener("endpoint-request-notification-count-updated", updateCount);
    };
  }, [userIsAdmin]);

  const loadStats = React.useCallback(async () => {
    if (!userIsAdmin) return;
    
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats({
        requested:
          (response.data.status_counts.pending || 0) +
          (response.data.status_counts.in_progress || 0),
        completed: response.data.status_counts.completed || 0,
        total: response.data.total_requests || 0,
      });
    } catch (error: unknown) {
      console.error("Error loading stats:", error);
      setStats({
        requested: requests.filter((r) => r.status === "pending" || r.status === "in_progress").length,
        completed: requests.filter((r) => r.status === "completed").length,
        total: requests.length,
      });
    }
  }, [userIsAdmin, requests]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (userIsAdmin && requests.length > 0) {
      loadStats();
    }
  }, [requests.length, userIsAdmin, loadStats]);

  useEffect(() => {
    if (!userIsAdmin) return;

    const handleNotification = (event: CustomEvent) => {
      const notification = event.detail;
      if (notification.type === "request_created") {
        toast.info(`New endpoint request: ${notification.component} - ${notification.task}`, {
          autoClose: 5000,
        });
      } else if (notification.type === "request_completed") {
        toast.success(`Endpoint completed: ${notification.component} - Endpoint: ${notification.endpoint}`, {
          autoClose: 6000,
        });
      }
    };

    window.addEventListener("endpoint-request-notification", handleNotification as EventListener);
    return () => {
      window.removeEventListener("endpoint-request-notification", handleNotification as EventListener);
    };
  }, [userIsAdmin]);

  const loadLeaderboard = React.useCallback(async () => {
    if (!userIsAdmin) return;
    
    setLoadingLeaderboard(true);
    try {
      const response = await apiService.get<{
        date_awarded: string;
        leaderboard: Array<{
          user_id: number;
          name: string;
          requests_count: number;
          awarded_points: number;
        }>;
      }>("/api/endpoint-request/endpoint-requests/daily-leaderboard/");

      setLeaderboard(response.data);
    } catch (error: unknown) {
      console.error("Error loading leaderboard:", error);
      toast.error("Failed to load leaderboard");
    } finally {
      setLoadingLeaderboard(false);
    }
  }, [userIsAdmin]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const filteredRequests = useMemo(() => {
    let filtered = [...requests];

    if (activeTab === "requested") {
      filtered = filtered.filter(
        (r) => r.status === "pending" || r.status === "in_progress"
      );
    } else if (activeTab === "completed") {
      filtered = filtered.filter((r) => r.status === "completed");
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.createdBy?.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [requests, activeTab, statusFilter, searchTerm]);

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleAwardPoints = async () => {
    if (!selectedDeveloper || pointsToAward <= 0) {
      toast.error("Please select a developer and enter valid points");
      return;
    }

    if (pointsToAward > 3) {
      toast.error("Cannot award more than 3 points");
      return;
    }

    try {
      const userId = parseInt(selectedDeveloper, 10);
      
      if (isNaN(userId)) {
        toast.error("Invalid developer selected");
        return;
      }

      const developer = leaderboard?.leaderboard.find(
        (dev) => dev.user_id === userId
      );

      if (!developer) {
        toast.error("Developer not found in leaderboard");
        return;
      }

      const today = new Date().toISOString().split("T")[0];

      await apiService.post("/api/endpoint-request/endpoint-requests/developer-points/", {
        developer: userId,
        date_awarded: today,
        awarded_points: pointsToAward,
        reason_for_award: pointsReason || "Awarded by admin",
      });

      toast.success(
        `Awarded ${pointsToAward} points to ${developer.name}${pointsReason ? `: ${pointsReason}` : ""}`
      );

      setOpenPointsDialog(false);
      setSelectedDeveloper("");
      setPointsToAward(0);
      setPointsReason("");

      await loadLeaderboard();

      window.dispatchEvent(
        new CustomEvent("developer-points-updated", {
          detail: { username: developer.name, user_id: developer.user_id },
        })
      );
    } catch (error: unknown) {
      console.error("Error awarding points:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to award points. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
  };

  if (!userIsAdmin) {
    return null;
  }

  const getRankBadge = (rank: number) => {
    if (rank === 0) return "🥇";
    if (rank === 1) return "🥈";
    if (rank === 2) return "🥉";
    return `#${rank + 1}`;
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Hero Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3.5,
          mb: 3.5,
          borderRadius: "20px",
          background: `linear-gradient(135deg, ${primaryColor}1F 0%, ${primaryColor}0D 100%)`,
          border: `1px solid ${primaryColor}30`,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.04)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box
            sx={{
              p: 1.8,
              borderRadius: "16px",
              backgroundColor: primaryColor,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 8px 20px ${primaryColor}40`,
            }}
          >
            <AdminIcon sx={{ fontSize: 34 }} />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="h4" fontWeight="800" sx={{ color: "text.primary", letterSpacing: "-0.5px" }}>
                Admin Dashboard
              </Typography>
              {notificationCount > 0 && (
                <Chip
                  label={`${notificationCount} New`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 700, borderRadius: "12px", height: 24 }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Manage endpoint requests, track progress, and award developer points
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<StarIcon sx={{ color: "#F59E0B" }} />}
          onClick={() => setOpenPointsDialog(true)}
          sx={{
            backgroundColor: primaryColor,
            color: "white",
            "&:hover": {
              backgroundColor: primaryColor,
              opacity: 0.92,
              transform: "translateY(-2px)",
              boxShadow: `0 8px 20px ${primaryColor}50`,
            },
            borderRadius: "12px",
            px: 3,
            py: 1.25,
            fontWeight: 700,
            textTransform: "none",
            transition: "all 0.2s ease",
            boxShadow: `0 4px 12px ${primaryColor}35`,
          }}
        >
          Award Points
        </Button>
      </Paper>

      {/* Stats Widget Grid */}
      <Grid container spacing={3} sx={{ mb: 3.5 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ModernStatCard
            title="Requested Endpoints"
            value={stats.requested}
            icon={<PendingIcon sx={{ fontSize: 28 }} />}
            accentColor="#F59E0B"
            subtext="Awaiting response or in development"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ModernStatCard
            title="Completed Endpoints"
            value={stats.completed}
            icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
            accentColor="#10B981"
            subtext="Deployed and active endpoints"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ModernStatCard
            title="Total System Requests"
            value={stats.total}
            icon={<TrophyIcon sx={{ fontSize: 28 }} />}
            accentColor={primaryColor}
            subtext="Lifetime requests logged"
          />
        </Grid>
      </Grid>

      {/* Developer Leaderboard Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3.5,
          borderRadius: "20px",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.04)",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrophyIcon sx={{ color: "#F59E0B" }} />
            <Typography variant="h6" fontWeight="700">
              Developer Leaderboard
            </Typography>
          </Box>
          {leaderboard?.date_awarded && (
            <Chip
              label={`Date: ${new Date(leaderboard.date_awarded).toLocaleDateString()}`}
              size="small"
              variant="outlined"
              sx={{ borderRadius: "8px", fontWeight: 600 }}
            />
          )}
        </Box>

        {loadingLeaderboard ? (
          <Box sx={{ py: 3 }}>
            <LinearProgress sx={{ borderRadius: "4px", height: 6 }} />
          </Box>
        ) : leaderboard && leaderboard.leaderboard.length > 0 ? (
          <Grid container spacing={2}>
            {leaderboard.leaderboard.slice(0, 5).map((dev, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={dev.user_id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    textAlign: "center",
                    position: "relative",
                    background:
                      index === 0
                        ? "linear-gradient(135deg, #FEF3C7 0%, #FFFBEB 100%)"
                        : "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
                    border:
                      index === 0
                        ? "1.5px solid #F59E0B"
                        : "1px solid rgba(226, 232, 240, 0.8)",
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "translateY(-3px)" },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 12,
                      fontSize: "1.1rem",
                      fontWeight: 800,
                    }}
                  >
                    {getRankBadge(index)}
                  </Box>

                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      mx: "auto",
                      mb: 1,
                      backgroundColor: index === 0 ? "#F59E0B" : primaryColor,
                      fontWeight: 700,
                      boxShadow: index === 0 ? "0 4px 10px rgba(245, 158, 11, 0.3)" : "none",
                    }}
                  >
                    {dev.name.charAt(0).toUpperCase()}
                  </Avatar>

                  <Typography variant="body1" fontWeight="700" noWrap sx={{ fontSize: "0.95rem" }}>
                    {dev.name}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {dev.requests_count} {dev.requests_count === 1 ? "request" : "requests"}
                  </Typography>

                  <Chip
                    icon={<StarIcon sx={{ fontSize: 16, color: "#F59E0B !important" }} />}
                    label={`${dev.awarded_points} pts`}
                    size="small"
                    sx={{
                      backgroundColor: "white",
                      fontWeight: 700,
                      color: "#B45309",
                      border: "1px solid #FDE68A",
                      borderRadius: "10px",
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              px: 2,
              borderRadius: "16px",
              backgroundColor: "#F8FAFC",
              border: "1px dashed #CBD5E1",
            }}
          >
            <TrophyIcon sx={{ fontSize: 48, color: "#94A3B8", mb: 1 }} />
            <Typography variant="body1" fontWeight="600" color="text.secondary">
              No Leaderboard Data Yet
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Points awarded by admins will appear here daily.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Tabs & Controls Container */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: "16px",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.02)",
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          sx={{
            px: 2,
            pt: 1,
            borderBottom: "1px solid #E2E8F0",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              minHeight: 52,
              px: 3,
              borderRadius: "10px 10px 0 0",
              color: "text.secondary",
              "&.Mui-selected": {
                color: primaryColor,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: primaryColor,
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Requested
                <Chip
                  label={stats.requested}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: activeTab === "requested" ? `${primaryColor}20` : "#F1F5F9",
                    color: activeTab === "requested" ? primaryColor : "#64748B",
                  }}
                />
              </Box>
            }
            value="requested"
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Completed
                <Chip
                  label={stats.completed}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    backgroundColor: activeTab === "completed" ? "#ECFDF5" : "#F1F5F9",
                    color: activeTab === "completed" ? "#047857" : "#64748B",
                  }}
                />
              </Box>
            }
            value="completed"
          />
        </Tabs>

        {/* Filters Bar */}
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search by component, task, lead, or endpoint..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value as RequestStatus | "all");
                    setPage(0);
                  }}
                  sx={{ borderRadius: "12px" }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setStatusFilter("all");
                  setSearchTerm("");
                  setPage(0);
                }}
                sx={{
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  py: 0.8,
                  borderColor: "#CBD5E1",
                  color: "text.secondary",
                  "&:hover": { borderColor: "#94A3B8" },
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Main Data Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(226, 232, 240, 0.9)",
          boxShadow: "0 4px 20px -2px rgba(0,0,0,0.04)",
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Component</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Task Description</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Developer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }} align="center">Image</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" fontWeight="600" color="text.secondary">
                      No matching endpoint requests found
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Try adjusting your search query or filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow
                    key={request.id || `request-${request.component}-${request.task}`}
                    hover
                    sx={{
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "#F1F5F9" },
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontWeight="700" color="text.primary">
                        {request.component}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title={request.task}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 280,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "text.primary",
                          }}
                        >
                          {request.task}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 26,
                            height: 26,
                            fontSize: "0.75rem",
                            backgroundColor: `${primaryColor}20`,
                            color: primaryColor,
                            fontWeight: 700,
                          }}
                        >
                          {(request.createdBy || request.lead || "U").charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                          {request.createdBy || request.lead || "Unknown"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ModernMethodChip method={request.method} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                            fontSize: "0.78rem",
                            maxWidth: 160,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "text.primary",
                          }}
                        >
                          {request.endpoint || "Unassigned"}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <ModernStatusBadge status={request.status} />
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <ModernPriorityChip priority={request.priority} />
                    </TableCell>

                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                        {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      {request.imageUrl ? (
                        <Tooltip title="View Attachment Image">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (request.imageUrl) {
                                setPreviewImageUrl(request.imageUrl);
                                setImagePreviewOpen(true);
                              }
                            }}
                            sx={{
                              color: primaryColor,
                              backgroundColor: `${primaryColor}10`,
                              "&:hover": { backgroundColor: `${primaryColor}25` },
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                          —
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center" sx={{ py: 2 }}>
                      <Tooltip title="View Full Details">
                        <IconButton
                          size="small"
                          onClick={() => onViewRequest(request)}
                          sx={{
                            color: primaryColor,
                            backgroundColor: `${primaryColor}10`,
                            "&:hover": { backgroundColor: `${primaryColor}25` },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid #E2E8F0" }}
        />
      </Paper>

      {/* Award Points Dialog */}
      <Dialog
        open={openPointsDialog}
        onClose={() => setOpenPointsDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1,
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "12px",
                backgroundColor: "#FEF3C7",
                color: "#B45309",
                display: "flex",
              }}
            >
              <StarIcon />
            </Box>
            <Typography variant="h6" fontWeight="700">
              Award Points to Developer
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2.5, borderRadius: "12px" }}>
            Points awarded will update the developer&apos;s daily leaderboard standing. Maximum of 3 points per award.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Select Developer</InputLabel>
            <Select
              value={selectedDeveloper}
              label="Select Developer"
              onChange={(e) => setSelectedDeveloper(e.target.value)}
              disabled={!leaderboard || leaderboard.leaderboard.length === 0}
              sx={{ borderRadius: "12px" }}
            >
              {leaderboard?.leaderboard.map((dev) => (
                <MenuItem key={dev.user_id} value={dev.user_id.toString()}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    {dev.name} ({dev.requests_count} requests • {dev.awarded_points} points)
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Points to Award"
            type="number"
            value={pointsToAward}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              const clampedValue = !isNaN(value) && value >= 0 ? Math.min(value, 3) : 0;
              setPointsToAward(clampedValue);
            }}
            inputProps={{ min: 1, max: 3 }}
            sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            helperText="Enter number of points (1 to 3)"
            error={pointsToAward > 3}
          />

          <TextField
            fullWidth
            label="Reason (Optional)"
            value={pointsReason}
            onChange={(e) => setPointsReason(e.target.value)}
            multiline
            rows={3}
            placeholder="e.g., Outstanding work on authentication and booking endpoint requests"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => setOpenPointsDialog(false)}
            sx={{ borderRadius: "10px", color: "text.secondary", textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAwardPoints}
            variant="contained"
            disabled={!selectedDeveloper || pointsToAward <= 0 || pointsToAward > 3}
            sx={{
              backgroundColor: primaryColor,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
            }}
          >
            Award Points
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setPreviewImageUrl(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ backgroundColor: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="700">
              Attachment Preview
            </Typography>
            <IconButton
              onClick={() => {
                setImagePreviewOpen(false);
                setPreviewImageUrl(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: "center", backgroundColor: "#0F172A" }}>
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="Endpoint context attachment"
              onError={() => {
                toast.error("Failed to load image preview");
              }}
              sx={{
                maxHeight: "75vh",
                maxWidth: "100%",
                objectFit: "contain",
                borderRadius: "12px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
